import React, { createContext, useState, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { checkApiHealth, loginUserByUrl } from "../qwanyx-core/api-client";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [logged, setLogged] = useState(false);
  
  // Debug logged state changes
  useEffect(() => {
    console.log("🔵 LOGGED STATE CHANGED:", logged, currentUser?.email);
  }, [logged, currentUser]);
  const [initializing, setInitializing] = useState(true);
  const location = useLocation();

  // Default guest user for development
  const DEFAULT_GUEST_USER = {
    id: "guest-user",
    email: "guest@qwanyx.com",
    profile: {
      name: "Guest User",
      email: "guest@qwanyx.com"
    }
  };

  /**
   * Handle URL-based login - now with real API authentication
   */
  const handleLoginRoute = async (route) => {
    if (route.startsWith('/login/')) {
      const [, userCredentials] = route.split('/login/');
      const [email, password] = userCredentials.split(':');
      try {
        console.log(`Attempting login for: ${email}`);
        
        // Use the API to authenticate
        const user = await loginUserByUrl(email, password);
        console.log('User data:', user);
        
        // Convert API user to frontend user format
        const authenticatedUser = {
          id: user._id,
          email: user.email,
          profile: { 
            name: user.name || user.email, 
            email: user.email 
          },
          userType: user.userType,
          status: user.status
        };
        
        setCurrentUser(authenticatedUser);
        setLogged(true);
        console.log(`Login successful. User: ${email}`, authenticatedUser);
        return authenticatedUser;
      } catch (error) {
        console.error('Login failed:', error.message);
        // If login fails, fall back to guest
        await autoLoginGuest();
        throw error;
      }
    }
  };

  /**
   * Handle logout
   */
  const handleLogoutRoute = async (route) => {
    if (route === '/logout') {
      try {
        // Reset to guest user
        await autoLoginGuest();
        setLogged(false);
        console.log('Logout successful. Logged in as guest.');
      } catch (error) {
        console.error('Logout failed:', error.message);
        throw error;
      }
    }
  };

  /**
   * Automatically log in guest if no user exists
   */
  const autoLoginGuest = async () => {
    try {
      console.log("Setting up guest user...");
      setCurrentUser(DEFAULT_GUEST_USER);
      console.log("Guest user set up successfully");
    } catch (error) {
      console.error("Guest user setup failed:", error.message);
    }
  };

  /**
   * Handle route changes and trigger login/logout handlers
   */
  useEffect(() => {
    const route = location.pathname;
    console.log(`Current route: ${route}`);

    if (route.startsWith('/login/')) {
      handleLoginRoute(route);
    } else if (route === '/logout') {
      handleLogoutRoute(route);
    }
  }, [location]);

  /**
   * Initialization logic - NO AUTO-LOGIN, just health check
   */
  useEffect(() => {
    const initialize = async () => {
      try {
        // Check API health
        const health = await checkApiHealth();
        if (health.status === 'healthy') {
          console.log('API is healthy, ready to go!');
        }
        
        // NO AUTO-LOGIN - wait for explicit login
        console.log('🔵 Initialization complete, waiting for login...');
        
        setInitializing(false);
      } catch (error) {
        console.error("Initialization failed:", error);
        setInitializing(false);
      }
    };

    // Initialize ONLY ONCE at app startup
    initialize();
  }, []); // Empty dependency array = run only once

  const value = {
    currentUser,
    logged,
    // Simplified auth functions for development
    signUp: async (email, password) => {
      console.log("SignUp not implemented yet");
    },
    resendConfirmationEmail: async (email) => {
      console.log("ResendConfirmationEmail not implemented yet");
    },
    sendResetPasswordEmail: async (email) => {
      console.log("SendResetPasswordEmail not implemented yet");
    },
    resetPassword: async (newPassword, token, tokenId) => {
      console.log("ResetPassword not implemented yet");
    },
  };

  if (initializing) {
    return <div>Loading...</div>;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};